import React, { useEffect, useRef, useState } from 'react';

import { checkAnyUserType } from '@utils/Util';

import CommonImage from '@/components/CommonImage';
import LoaderComponent from '@/components/LoaderComponent';
import ModalContainer from '@/components/modals/ModalContainer';

import ActionButtons from './ActionButtons';
import CMSection from './CMSection';
import CommercialSection from './CommercialSection';
import CostingSummary from './CostingSummary';
import EmbroideryTable from './EmbroideryTable';
import FabricSustainabilityTable from './FabricSustainabilityTable';
import FabricTable from './FabricTable';
import PackagingSection from './PackagingSection';
import PrintTable from './PrintTable';
import ProductWisePriceRange from './ProductWisePriceRange';
import TestingSection from './TestingSection';
import TrimsSection from './TrimsSection';
import WashTable from './WashTable';
const SECTION_HIGHLIGHT_DURATION = 1500;

// render order of the costing tables, also used to look a single section up for the modal
const SECTION_ORDER = [
	'fiberRequirement',
	'fabricCost',
	'printCost',
	'embroideryCost',
	'washCost',
	'trimsCost',
	'packaging',
	'cmCost',
	'perPisTestingCost',
	'commercial'
];

const SECTION_TITLES = {
	fiberRequirement: 'Fabric Sustainability',
	fabricCost: 'Fabric Cost',
	printCost: 'Print Cost',
	embroideryCost: 'Embroidery Cost',
	washCost: 'Wash Cost',
	trimsCost: 'Trims Cost',
	packaging: 'Packaging',
	cmCost: 'CM Cost',
	perPisTestingCost: 'Testing',
	commercial: 'Commercial'
};

const CostingTableAndSummary = ({
	payload = {},
	responsePayload = {},
	updateFormData = () => {},
	isEditable,
	validationErrors,
	reCostIndividually = () => {},
	trackChangedField = () => {},
	hasChanges,
	handleSubmit = () => {},
	hasSubmittedCosting,
	onReCost,
	historyId,
	isLiked,
	handleLikeUnlike = () => {},
	isLikeUnlikeLoading,
	costingBrandInfo,
	priceRangeData,
	isPriceRangeLoading,
	productInfoData,
	showActionButtons = true,
	showSummaryActionButtons = true,
	isProductDevelopment = false,
	productionIsLoading = false,
	headerOrderQuantity,
	// the optimize page's payload is a scenario, which doesn't carry the qty-wise list the header saves to
	quantityWisePrices,
	baseQty,
	onQtyWisePricesSaved
}) => {
	const sectionRefs = useRef({});
	const [activeSection, setActiveSection] = useState(null);
	const [modalSectionKey, setModalSectionKey] = useState(null);

	useEffect(() => {
		if (!activeSection) {
			return;
		}
		const timer = setTimeout(() => setActiveSection(null), SECTION_HIGHLIGHT_DURATION);

		return () => clearTimeout(timer);
	}, [activeSection]);

	// .costing-section has scroll-margin-top, so the sticky header never covers the section
	const scrollToSection = (sectionKey) => {
		sectionRefs.current[sectionKey]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
		setActiveSection(sectionKey);
	};

	const sectionProps = (sectionKey) => ({
		ref: (node) => (sectionRefs.current[sectionKey] = node),
		className: `costing-section${activeSection === sectionKey ? ' costing-section-active' : ''}`
	});

	const editableRoles = () => {
		return checkAnyUserType(['PRODUCT_DEVELOPMENT', 'COSTING_MANAGER']);
	};

	const isPdAndCostingManager = editableRoles();

	const hasFiberRequirement =
		payload?.fiberRequirementList &&
		Array.isArray(payload?.fiberRequirementList) &&
		payload?.fiberRequirementList.length > 0;

	// one place to build a section, so the inline table and the modal stay identical
	const renderSection = (sectionKey) => {
		switch (sectionKey) {
			case 'fiberRequirement':
				return hasFiberRequirement ? (
					<FabricSustainabilityTable
						data={payload?.fiberRequirementList}
						onChange={(data) => updateFormData('fiberRequirementList', data)}
						isEditable={isEditable}
					/>
				) : null;
			case 'fabricCost':
				return (
					<FabricTable
						data={payload?.fabricWisePriceBreakdownList}
						cadConsumptions={payload?.cadWiseConsumptionList}
						fabricWiseMeasurements={payload?.fabricWiseMeasurements}
						totalCost={payload?.fabricCost || 0}
						measurementSizeRange={payload?.measurementSizeRange || ''}
						selectedMeasurementSize={payload?.selectedMeasurementSize || ''}
						onChange={(data) => updateFormData('fabricWisePriceBreakdownList', data)}
						onMeasurementsChange={(data) => updateFormData('fabricWiseMeasurements', data)}
						onConsumptionTypeChange={(data) => updateFormData('selectedConsumptionType', data)}
						isEditable={isEditable}
						validationErrors={validationErrors}
						onReCost={reCostIndividually}
						onFieldChange={trackChangedField}
						selectedConsumptionType={payload?.selectedConsumptionType || ''}
					/>
				);
			case 'printCost':
				return (
					<PrintTable
						data={payload?.printPriceList}
						totalCost={payload?.printCost || 0}
						onChange={(data) => updateFormData('printPriceList', data)}
						isEditable={isEditable}
						validationErrors={validationErrors}
						onFieldChange={trackChangedField}
					/>
				);
			case 'embroideryCost':
				return (
					<EmbroideryTable
						data={payload?.embroideryPriceList}
						totalCost={payload?.embroideryCost || 0}
						onChange={(data) => updateFormData('embroideryPriceList', data)}
						isEditable={isEditable}
						validationErrors={validationErrors}
						onFieldChange={trackChangedField}
					/>
				);
			case 'washCost':
				return (
					<WashTable
						data={payload?.washPriceBreakdown}
						totalCost={payload?.washCost || 0}
						onChange={(data) => updateFormData('washPriceBreakdown', data)}
						isEditable={isEditable}
						validationErrors={validationErrors}
						onFieldChange={trackChangedField}
					/>
				);
			case 'trimsCost':
				return (
					<TrimsSection
						data={payload?.trimsDetailsList}
						totalCost={payload?.trimsCost || 0}
						onChange={(data) => updateFormData('trimsDetailsList', data)}
						trimsAdditionalCharge={payload?.trimsAdditionalCharge}
						onTrimsAdditionalChargeChange={(value) =>
							updateFormData('trimsAdditionalCharge', value)
						}
						isEditable={isEditable}
						onFieldChange={trackChangedField}
					/>
				);
			case 'packaging':
				return (
					<PackagingSection
						data={payload?.packagingPriceCostBreakdown}
						totalCost={payload?.packagingCost || 0}
						onChange={(data) => updateFormData('packagingPriceCostBreakdown', data)}
						isEditable={isEditable}
						onFieldChange={trackChangedField}
					/>
				);
			case 'cmCost':
				return (
					<CMSection
						value={payload?.cmCost}
						totalCost={responsePayload?.cmCost || payload?.cmCost || 0}
						onChange={(value) => updateFormData('cmCost', value)}
						isEditable={isEditable}
						onFieldChange={trackChangedField}
					/>
				);
			case 'perPisTestingCost':
				return (
					<TestingSection
						perStyleTestingCost={payload?.perStyleTestingCost}
						totalCost={payload?.perPisTestingCost || 0}
						onChange={(value) => updateFormData('perStyleTestingCost', value)}
						isEditable={isEditable}
						onFieldChange={trackChangedField}
					/>
				);
			case 'commercial':
				return (
					<CommercialSection
						value={payload?.commercialCostPercentage}
						totalCost={payload?.commercialCost || 0}
						onChange={(value) => updateFormData('commercialCostPercentage', value)}
						isEditable={isEditable}
						onFieldChange={trackChangedField}
					/>
				);
			default:
				return null;
		}
	};

	// only PD / costing manager get the full table to scroll to — everyone else gets the section in a modal
	const handleSectionClick = (sectionKey) => {
		if (isPdAndCostingManager) {
			scrollToSection(sectionKey);
			return;
		}
		setModalSectionKey(sectionKey);
	};

	return (
		<LoaderComponent loading={productionIsLoading}>
			<div
				className={`costing-table-and-summary d-flex flex-wrap gap-3 ${isProductDevelopment ? 'mt-4' : ''}`}
			>
				{isPdAndCostingManager && (
					<div className="all-costing-table mb-4">
						{SECTION_ORDER.map((sectionKey) => {
							const section = renderSection(sectionKey);
							if (!section) return null;

							return (
								<div key={sectionKey} {...sectionProps(sectionKey)}>
									{section}
								</div>
							);
						})}
					</div>
				)}
				<div
					className={`costing-summary-container ${!isPdAndCostingManager ? 'w-100' : ''}${isProductDevelopment ? 'with-product-development' : ''}`}
				>
					{showActionButtons && isPdAndCostingManager && (
						<ActionButtons
							hasChanges={hasChanges}
							onSubmit={handleSubmit}
							isEditable={isEditable}
							hasSubmittedCosting={hasSubmittedCosting}
							onReCost={onReCost}
						/>
					)}
					<CostingSummary
						summary={{
							fabricConsumption: payload?.estimatedConsumption || 0,
							fabricCost: payload?.fabricCost || 0,
							printCost: payload?.printCost || 0,
							embroideryCost: payload?.embroideryCost || 0,
							trimsCost: payload?.trimsCost || 0,
							cmCost: responsePayload?.cmCost || payload?.cmCost || 0,
							washCost: payload?.washCost || 0,
							packaging: payload?.packagingCost || 0,
							commercial: payload?.commercialCost || 0,
							perPisTestingCost: payload?.perPisTestingCost || 0
						}}
						historyId={historyId}
						isLiked={isLiked}
						onLike={() => handleLikeUnlike(true)}
						onDislike={() => handleLikeUnlike(false)}
						isLoading={isLikeUnlikeLoading}
						totalFobPerUnit={payload?.predictedCost || 0}
						baseQty={baseQty ?? payload?.quantity}
						costingBrandInfo={costingBrandInfo}
						quantityWisePrices={quantityWisePrices || payload?.qtyWisePriceList || []}
						headerOrderQuantity={headerOrderQuantity}
						onSectionClick={handleSectionClick}
						costingId={payload?.id}
						onQtyWisePricesSaved={onQtyWisePricesSaved}
						isPdAndCostingManager={isPdAndCostingManager}
						hasChanges={hasChanges}
						handleSubmit={handleSubmit}
						isEditable={isEditable}
						hasSubmittedCosting={hasSubmittedCosting}
						onReCost={onReCost}
						showSummaryActionButtons={showSummaryActionButtons}
					/>
					<ProductWisePriceRange
						priceRangeData={priceRangeData?.payload || {}}
						isPriceRangeLoading={isPriceRangeLoading}
						predictedCost={payload?.predictedCost || 0}
						category={productInfoData?.subCategoryResponse?.name}
						isPdAndCostingManager={isPdAndCostingManager}
					/>
				</div>
			</div>
			{modalSectionKey && (
				<ModalContainer
					className="summary-wise-modal width-784-dialog"
					onCloseModal={() => setModalSectionKey(null)}
				>
					<div className="popup-wrapper">
						<div className="common-popup body-scroll">
							<div className="common-popup-header d-flex justify-content-between">
								<div className="popup-tilte">
									<h3 className="semibold-16 mb-0">
										{SECTION_TITLES[modalSectionKey] || ''}
									</h3>
								</div>
								<div className="close-btn" onClick={() => setModalSectionKey(null)}>
									<CommonImage alt="close" src="/icons/close.svg" />
								</div>
							</div>
							<div className="common-popup-body scroll-y-label">
								{renderSection(modalSectionKey)}
							</div>
							<div className="common-popup-footer d-flex justify-content-end p-3">
								<button
									className="button size32"
									disabled={!hasChanges}
									onClick={async () => {
										await onReCost?.();
										setModalSectionKey(null);
									}}
								>
									<span className="regular-14 weight-500">Re-Cost</span>
								</button>
							</div>
						</div>
					</div>
				</ModalContainer>
			)}
		</LoaderComponent>
	);
};

export default CostingTableAndSummary;
